import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircle2, Loader2, XCircle, Copy, KeyRound, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const fmtEur = (n, currency = "EUR") =>
  Number(n || 0).toLocaleString("es-ES", { style: "currency", currency, maximumFractionDigits: 2 });

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "checking", data: null, error: null, attempts: 0 });
  const stoppedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error", error: "Falta el identificador de sesión" });
      return;
    }
    let cancelled = false;

    const poll = async (attempt = 0) => {
      if (cancelled || stoppedRef.current) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          stoppedRef.current = true;
          setState({ phase: "paid", data, error: null, attempts: attempt });
          return;
        }
        if (data.status === "expired") {
          stoppedRef.current = true;
          setState({ phase: "expired", data, error: null, attempts: attempt });
          return;
        }
        if (attempt >= 30) {
          stoppedRef.current = true;
          setState({ phase: "timeout", data, error: null, attempts: attempt });
          return;
        }
        setState({ phase: "checking", data, error: null, attempts: attempt });
        setTimeout(() => poll(attempt + 1), 2000);
      } catch (e) {
        if (cancelled) return;
        stoppedRef.current = true;
        setState({ phase: "error", error: e?.response?.data?.detail || "Error consultando el estado del pago", attempts: attempt });
      }
    };
    poll(0);
    return () => { cancelled = true; };
  }, [sessionId]);

  const copyCode = (code) => {
    navigator.clipboard.writeText(code).then(() => toast.success("Código copiado al portapapeles"));
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center p-8" data-testid="payment-success-page">
      <div className="bg-white border border-slate-200 rounded-2xl p-10 max-w-xl w-full text-center shadow-sm">
        {state.phase === "checking" && (
          <>
            <Loader2 className="h-12 w-12 mx-auto text-sky-500 animate-spin" />
            <h1 className="font-display text-3xl font-extrabold mt-4">Confirmando tu pago…</h1>
            <p className="text-slate-500 mt-2">Esto suele tardar unos segundos. Estamos comprobando con Stripe.</p>
            <div className="text-xs text-slate-400 mt-4">Intentos: {state.attempts + 1} / 30</div>
          </>
        )}
        {state.phase === "paid" && state.data?.license && (
          <>
            <div className="mx-auto h-16 w-16 rounded-full bg-emerald-100 grid place-items-center">
              <CheckCircle2 className="h-9 w-9 text-emerald-600" />
            </div>
            <h1 className="font-display text-3xl font-extrabold mt-4" data-testid="payment-success-title">¡Pago completado!</h1>
            <p className="text-slate-500 mt-2">
              Hemos cobrado <strong>{fmtEur((state.data.amount_total || 0) / 100, (state.data.currency || "eur").toUpperCase())}</strong> y generado tu licencia.
            </p>
            <div className="mt-6 bg-slate-50 border border-slate-200 rounded-xl p-5 text-left">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold mb-3">
                <KeyRound className="h-3.5 w-3.5" /> Tu licencia
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Código</div>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="font-mono text-base font-bold" data-testid="license-code">{state.data.license.code}</code>
                    <button
                      onClick={() => copyCode(state.data.license.code)}
                      className="p-1.5 rounded-md text-slate-500 hover:bg-slate-200"
                      data-testid="copy-code"
                      title="Copiar código"
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Contraseña inicial</div>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="font-mono text-base font-bold" data-testid="license-password">{state.data.license.password}</code>
                    <button
                      onClick={() => copyCode(state.data.license.password)}
                      className="p-1.5 rounded-md text-slate-500 hover:bg-slate-200"
                      title="Copiar contraseña"
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </div>
              <div className="text-[11px] text-slate-500 mt-3">
                Guárdala en un lugar seguro. La podrás usar para entrar en la app desde la pantalla de login.
              </div>
            </div>
            <Link
              to="/leads"
              className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800"
              data-testid="back-to-leads"
            >
              Ver bandeja de solicitudes <ChevronRight className="h-4 w-4" />
            </Link>
          </>
        )}
        {(state.phase === "expired" || state.phase === "timeout" || state.phase === "error") && (
          <>
            <div className="mx-auto h-16 w-16 rounded-full bg-rose-100 grid place-items-center">
              <XCircle className="h-9 w-9 text-rose-600" />
            </div>
            <h1 className="font-display text-3xl font-extrabold mt-4">
              {state.phase === "expired" ? "Sesión de pago expirada" : state.phase === "timeout" ? "Aún no se ha confirmado" : "No se pudo confirmar"}
            </h1>
            <p className="text-slate-500 mt-2">
              {state.phase === "timeout"
                ? "Si has completado el pago, vuelve a abrir el enlace en unos segundos. Stripe puede tardar un poco más."
                : state.error || "Vuelve a iniciar la operación desde la bandeja de Solicitudes."}
            </p>
            <Link to="/leads" className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800">
              Volver a Solicitudes
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
